import { useMutation, useQuery } from "@tanstack/react-query";
import { AssetMediaApi } from "../api/asset-media-api";
import { AssetMediaDto } from "@repo/api/asset-media";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";
import { isSafari, sleep } from "../utils";
import { isSvgFile, svgFileToPng } from "../svg-to-png";

const PROCESSING_TIMEOUT_MS = 120_000;
const PROCESSING_POLL_INTERVAL_MS = 2_000;

export function useTeamAssetMediasQuery(teamExternalId: string) {
  return useQuery({
    queryKey: assetMediaQueryKeys.getTeamAssetMedias(teamExternalId),
    queryFn: () => AssetMediaApi.getTeamAssetMedias(teamExternalId),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch asset medias",
    },
  });
}

export function useUploadAssetMediasMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: AssetMediaDto[]) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: assetMediaQueryKeys.uploadAssetMedias(teamExternalId),
    mutationFn: async (files: File[]) => {
      const preparedFiles = await Promise.all(
        files.map((file) => (isSvgFile(file) ? svgFileToPng(file) : file)),
      );

      const results = await Promise.all(
        preparedFiles.map(async (file) => {
          const assetMedia = await AssetMediaApi.uploadAssetMedia(
            teamExternalId,
            file,
          );

          if (assetMedia.status === "PROCESSING") {
            const toastMessage = isSafari()
              ? "Safari does not support transparent videos. Switch to Chrome for the best experience."
              : "Transparent video detected. Uploading may take slightly longer.";

            Toast.info(toastMessage, { duration: 10000 });

            const startedAt = Date.now();

            while (Date.now() - startedAt < PROCESSING_TIMEOUT_MS) {
              await sleep(PROCESSING_POLL_INTERVAL_MS);

              const response = await AssetMediaApi.getAssetMediasByIds(
                teamExternalId,
                [assetMedia.externalId],
              );

              const updated = response[assetMedia.externalId];

              if (updated?.status === "COMPLETED") {
                return updated;
              } else if (updated?.status !== "PROCESSING") {
                throw new Error("Failed to process asset media");
              }
            }

            throw new Error("Asset media processing timed out");
          }

          if (assetMedia.status !== "COMPLETED") {
            throw new Error("Failed to process asset media");
          }

          return assetMedia;
        }),
      );
      return results;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: assetMediaQueryKeys.getTeamAssetMedias(teamExternalId),
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to upload media");
      onError?.(error);
    },
  });
}

export function useDeleteAssetMediasMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: assetMediaQueryKeys.deleteAssetMedias(teamExternalId),
    mutationFn: (externalIds: string[]) =>
      AssetMediaApi.deleteAssetMedias(teamExternalId, externalIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: assetMediaQueryKeys.getTeamAssetMedias(teamExternalId),
      });
      onSuccess?.();
    },
    onError: (error) => {
      Toast.error("Failed to delete media");
      onError?.(error);
    },
  });
}

export const assetMediaQueryKeys = {
  all: () => ["assetMedia"],
  getTeamAssetMedias: (teamExternalId: string) => [
    "assetMedia",
    "list",
    { teamExternalId },
  ],
  uploadAssetMedias: (teamExternalId: string) => [
    "assetMedia",
    "upload",
    { teamExternalId },
  ],
  syncTags: (teamExternalId: string) => [
    "assetMedia",
    "syncTags",
    { teamExternalId },
  ],
  deleteAssetMedias: (teamExternalId: string) => [
    "assetMedia",
    "delete",
    { teamExternalId },
  ],
};
